import type { User } from "firebase/auth";

/**
 * Hermetic dev-auth bypass for local development and automated browser tests.
 *
 * When `NEXT_PUBLIC_DEV_AUTH=1` the web app skips Firebase entirely:
 *  - `auth-context` synthesizes a signed-in user so `AuthGuard` passes, and
 *  - `apiFetch` sends an `X-Tenant-Id` header instead of a Firebase Bearer
 *    token.
 *
 * The API server must run with `RAILYARD_ALLOW_HEADER_AUTH=1` to accept it
 * (see `railyard.routes._deps.get_auth_context`). Never enable this in
 * production — it turns off authentication for the whole app.
 *
 * Workspace switching (`X-Active-Workspace`) is not honored in this mode;
 * the backend's header-auth path resolves the tenant straight from
 * `X-Tenant-Id`. Point `NEXT_PUBLIC_DEV_AUTH_TENANT` at the workspace you
 * want to test instead.
 */
export const DEV_AUTH_ENABLED = process.env.NEXT_PUBLIC_DEV_AUTH === "1";

/**
 * Sent as the `X-Tenant-Id` header. Defaults to the backend's
 * `DEFAULT_TENANT_ID` ("default") when unset.
 */
export const DEV_AUTH_TENANT_ID =
  process.env.NEXT_PUBLIC_DEV_AUTH_TENANT || "default";

/**
 * Sent as the `X-Dev-User` header. The backend's header-auth path resolves
 * (load-or-create) a real persisted **owner** account keyed off this email,
 * so admin-gated surfaces work in local QA. Override with
 * `NEXT_PUBLIC_DEV_AUTH_EMAIL`.
 */
export const DEV_AUTH_EMAIL =
  process.env.NEXT_PUBLIC_DEV_AUTH_EMAIL || "dev@railyard.local";

/**
 * A minimal stand-in for a Firebase `User`. The backend resolves identity
 * from the `X-Dev-User` header (to a persisted owner), so only the handful of
 * fields the UI actually reads need to be present here. `getIdToken` returns
 * an empty string — in dev-auth mode `apiFetch` authenticates with
 * `X-Tenant-Id` + `X-Dev-User`, not a Bearer token.
 */
export function makeDevUser(): User {
  return {
    uid: "dev-user",
    email: DEV_AUTH_EMAIL,
    displayName: "Dev User",
    emailVerified: true,
    isAnonymous: false,
    getIdToken: async () => "",
  } as unknown as User;
}
